/**
 * DealerStats — KPI-Übersicht für B2B-Händler.
 * Aggregate aus /users/me/dealer-stats: Inserate, Views, Favoriten,
 * Preisvorschläge, Reservierungen, Bewertungen, Top-Inserate.
 *
 * Jun-2026 (User-Wunsch) — Refactoring: Die früher monolithische Anzeige ist
 * jetzt über die ``sections``-Prop in eigenständige, EINKLAPPBARE Bereiche
 * aufgeteilt, damit das Cockpit jeden Block am passenden Arbeitsbereich zeigen
 * kann:
 *   • "sales"    → Verkaufs-/Inserate-Statistik (Aktive Inserate, Views, …)
 *   • "workshop" → Werkstatt-Übersicht
 *   • "finance"  → Rechnungs-Übersicht, €Punkte-Saldo, Monatsabrechnung,
 *                  Meine Rechnungen
 * Jeder Block lässt sich ein-/ausklappen (Status pro Händler in localStorage).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "../lib/api";
import { previewPdfResponse } from "../lib/pdfPreview";
import { Eye, Heart, Handshake, Clock, Star, Gavel, TrendingUp, Loader2, Package, Coins, FileText, Download, MessageSquare, Wrench, AlertCircle, Receipt, ChevronDown } from "lucide-react";
import DealerTipsCard from "./dashboard/DealerTipsCard";
import { useCollapse } from "../lib/cockpitCollapse";

// Jun-2026 — einklappbarer Statistik-Block mit einheitlichem Header.
// Der Ein-/Ausklapp-Zustand wird geräteübergreifend gespeichert (useCollapse).
function Section({ collapseKey, defaultCollapsed = true, icon: Icon, title, badge = 0, badgeLabel = "", right = null, testid, children }) {
    const [collapsed, toggle] = useCollapse(collapseKey, defaultCollapsed);
    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={testid}>
            <div className={`w-full flex items-center justify-between gap-2 ${!collapsed ? "border-b border-gray-100" : ""}`}>
                <button
                    type="button"
                    onClick={toggle}
                    className="flex-1 min-w-0 px-5 py-3 flex items-center gap-2 hover:bg-gray-50 transition-colors text-left"
                    data-testid={testid ? `${testid}-toggle` : undefined}
                    aria-expanded={!collapsed}
                >
                    <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                    {Icon && <Icon className="w-4 h-4 text-[#FFCC00] shrink-0" />}
                    <h3 className="font-display font-bold text-base truncate">{title}</h3>
                    {badge > 0 && (
                        <span className="inline-flex items-center justify-center h-4 px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-mono-num leading-none shrink-0" data-testid={testid ? `${testid}-badge` : undefined}>
                            {badge > 99 ? "99+" : badge}{badgeLabel ? ` ${badgeLabel}` : ""}
                        </span>
                    )}
                </button>
                {right && <span className="shrink-0 pr-5 text-[10px] uppercase tracking-wider text-gray-500 font-bold">{right}</span>}
            </div>
            {!collapsed && <div data-testid={testid ? `${testid}-body` : undefined}>{children}</div>}
        </div>
    );
}

function StatCard({ icon: Icon, label, value, sub, tone = "default", testid, onClick, active = false }) {
    const tones = {
        default: "bg-white border-gray-200",
        accent: "bg-[#FFCC00]/10 border-[#FFCC00]/40",
        success: "bg-green-50 border-green-200",
        muted: "bg-gray-50 border-gray-200",
    };
    const interactive = typeof onClick === "function";
    const activeRing = active ? " ring-2 ring-[#FFCC00] ring-offset-1" : "";
    const hover = interactive ? " cursor-pointer hover:border-[#FFCC00] hover:shadow-sm transition-all" : "";
    return (
        <button
            type={interactive ? "button" : undefined}
            onClick={interactive ? onClick : undefined}
            disabled={!interactive}
            className={`text-left w-full border rounded-sm p-4 ${tones[tone] || tones.default}${hover}${activeRing} ${interactive ? "" : "cursor-default"}`}
            data-testid={testid}
            aria-expanded={interactive ? active : undefined}
        >
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">
                <Icon className="w-3.5 h-3.5" /> {label}
                {interactive && <span className="ml-auto text-[9px] font-bold text-gray-400">{active ? "▾" : "▸"}</span>}
            </div>
            <div className="font-display font-black text-2xl text-[#111827] font-mono-num leading-none">{value}</div>
            {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
        </button>
    );
}

// Phase 3.36 — Reaktionszeit-Trend (Mini-Chart, letzte N Monate)
function ResponseTrendChart() {
    const [trend, setTrend] = useState(null);
    useEffect(() => {
        api.get("/users/me/dealer-stats/response-trend?months=12")
            .then((r) => setTrend(r.data?.trend || []))
            .catch(() => setTrend([]));
    }, []);
    if (trend === null) return null;
    const hasData = trend.some((m) => m.count > 0);
    if (!hasData) return null;
    const max = Math.max(...trend.map((m) => m.avg_hours || 0), 24);
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="response-trend-chart">
            <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-[#111827]" />
                <span className="text-sm font-bold uppercase tracking-wider text-[#111827]">Reaktionszeit-Trend (12 Monate)</span>
            </div>
            <div className="flex items-end gap-1 h-32">
                {trend.map((m) => {
                    const h = m.avg_hours;
                    const pct = h != null ? Math.max(4, Math.round((h / max) * 100)) : 0;
                    const isFast = h != null && h < 24;
                    return (
                        <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group">
                            <div className="text-[9px] font-mono-num text-gray-500 h-3">
                                {h != null ? `${h}h` : ""}
                            </div>
                            <div className="w-full flex-1 flex items-end">
                                <div
                                    className={`w-full rounded-t-sm transition-all ${h == null ? "bg-gray-100" : isFast ? "bg-green-500" : "bg-[#FFCC00]"}`}
                                    style={{ height: h != null ? `${pct}%` : "4px" }}
                                    title={`${m.month}: ${h != null ? `${h} h` : "keine Daten"} (${m.count} Bewertungen)`}
                                    data-testid={`trend-bar-${m.month}`}
                                />
                            </div>
                            <div className="text-[9px] font-bold text-gray-600">{m.month.slice(5)}</div>
                        </div>
                    );
                })}
            </div>
            <div className="text-[10px] text-gray-500 mt-2 flex items-center gap-3">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 inline-block"></span>schnell (&lt;24h)</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#FFCC00] inline-block"></span>länger</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-100 inline-block"></span>keine Daten</span>
            </div>
        </div>
    );
}

// Jul-2026 — Opt-out Toggle für das Compliance-Score-Widget im Dashboard.
function ComplianceWidgetToggle() {
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/auth/me").then((r) => setEnabled(r.data?.compliance_widget_enabled !== false)).catch(() => setEnabled(true));
    }, []);
    if (enabled === null) return null;
    const toggle = async () => {
        setSaving(true);
        const next = !enabled;
        try {
            await api.patch("/users/me/dealer-profile", { compliance_widget_enabled: next });
            setEnabled(next);
        } catch (e) {
            // optional toast
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-sm px-4 py-3 flex items-center justify-between gap-3" data-testid="compliance-widget-toggle">
            <div className="flex-1">
                <div className="text-sm font-bold text-[#111827]">Compliance-Score im Dashboard anzeigen</div>
                <div className="text-[11px] text-gray-600 mt-0.5">
                    Zeigt das Frühwarn-Widget (Score, Trend, offene Steuernachweise) im Dashboard — erscheint nur, wenn Umsätze (festgeschriebene Rechnungen) vorliegen.
                </div>
            </div>
            <label className="inline-flex items-center cursor-pointer gap-2 shrink-0">
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={toggle}
                    disabled={saving}
                    className="sr-only peer"
                    data-testid="compliance-widget-checkbox"
                />
                <span className="relative w-10 h-5 rounded-full bg-gray-300 peer-checked:bg-[#111827] transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-4 before:h-4 before:bg-white before:rounded-full before:transition-transform peer-checked:before:translate-x-5"></span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#111827] min-w-[24px]">{enabled ? "An" : "Aus"}</span>
            </label>
        </div>
    );
}

// Jun-2026 — Opt-in Toggle für wöchentliche Compliance-Lücken-Übersicht per E-Mail.
function ComplianceWeeklyEmailToggle() {
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/auth/me").then((r) => setEnabled(!!r.data?.compliance_email_weekly)).catch(() => setEnabled(false));
    }, []);
    if (enabled === null) return null;
    const toggle = async () => {
        setSaving(true);
        const next = !enabled;
        try {
            await api.patch("/users/me/dealer-profile", { compliance_email_weekly: next });
            setEnabled(next);
        } catch (e) {
            // optional toast
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="bg-amber-50 border border-amber-300 rounded-sm px-4 py-3 flex items-center justify-between gap-3" data-testid="compliance-email-weekly-toggle">
            <div className="flex-1">
                <div className="text-sm font-bold text-[#111827]">Wöchentliche Compliance-Übersicht per E-Mail</div>
                <div className="text-[11px] text-gray-600 mt-0.5">
                    Jeden Montag eine Zusammenfassung offener Steuernachweise (Gelangensnachweise, fehlende USt-IdNr., GoBD-Festschreibung, Reverse-Charge) — nur wenn Lücken bestehen.
                </div>
            </div>
            <label className="inline-flex items-center cursor-pointer gap-2 shrink-0">
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={toggle}
                    disabled={saving}
                    className="sr-only peer"
                    data-testid="compliance-email-weekly-checkbox"
                />
                <span className="relative w-10 h-5 rounded-full bg-gray-300 peer-checked:bg-[#111827] transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-4 before:h-4 before:bg-white before:rounded-full before:transition-transform peer-checked:before:translate-x-5"></span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#111827] min-w-[24px]">{enabled ? "An" : "Aus"}</span>
            </label>
        </div>
    );
}

// Phase 3.33 — Toggle für monatlichen Stats-PDF-Versand per E-Mail.
function MonthlyStatsEmailToggle() {
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/auth/me").then((r) => setEnabled(!!r.data?.stats_email_monthly)).catch(() => setEnabled(false));
    }, []);
    if (enabled === null) return null;
    const toggle = async () => {
        setSaving(true);
        const next = !enabled;
        try {
            await api.patch("/users/me/dealer-profile", { stats_email_monthly: next });
            setEnabled(next);
        } catch (e) {
            // optional toast
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/40 rounded-sm px-4 py-3 flex items-center justify-between gap-3" data-testid="stats-email-monthly-toggle">
            <div className="flex-1">
                <div className="text-sm font-bold text-[#111827]">Monatlicher Statistik-Report per E-Mail</div>
                <div className="text-[11px] text-gray-600 mt-0.5">
                    Wir senden Ihnen am 1. jedes Monats automatisch den Vormonats-PDF-Report (mit Reaktionszeit-KPI).
                </div>
            </div>
            <label className="inline-flex items-center cursor-pointer gap-2 shrink-0">
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={toggle}
                    disabled={saving}
                    className="sr-only peer"
                    data-testid="stats-email-monthly-checkbox"
                />
                <span className="relative w-10 h-5 rounded-full bg-gray-300 peer-checked:bg-[#111827] transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-4 before:h-4 before:bg-white before:rounded-full before:transition-transform peer-checked:before:translate-x-5"></span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#111827] min-w-[24px]">{enabled ? "An" : "Aus"}</span>
            </label>
        </div>
    );
}

// Feb-2026 (User-Wunsch 27.05.2026) — Drill-Down-Panel pro KPI-Card.
function KpiDrilldownPanel({ kpi, onClose, loading, items, extra }) {
    const titles = {
        active: "Aktive Inserate",
        views: "Top-Inserate nach Views",
        favorites: "Top-Inserate nach Favorisierungen",
        auctions: "Laufende Auktionen",
        offers: "Letzte Preisvorschläge",
        reservations: "Letzte Reservierungen",
        reviews: "Letzte Bewertungen",
        response_time: "Reaktionszeit-Trend",
        conversion: "Conversion-Erklärung",
    };
    const links = {
        active: { to: "/my-listings", label: "Alle Inserate" },
        views: { to: "/my-listings", label: "Alle Inserate" },
        favorites: { to: "/my-listings", label: "Alle Inserate" },
        auctions: { to: "/my-listings?type=auction", label: "Auktionen" },
        offers: { to: "/dashboard?tab=offers", label: "Alle Preisvorschläge" },
        reservations: { to: "/dashboard?tab=reservations", label: "Alle Reservierungen" },
        reviews: { to: "/dealer/reviews", label: "Alle Bewertungen" },
        response_time: { to: "/dealer/reviews", label: "Bewertungen" },
        conversion: null,
    };
    const linkInfo = links[kpi];
    const STATUS_DE = {
        pending: "offen", accepted: "angenommen", rejected: "abgelehnt",
        withdrawn: "zurückgezogen", countered: "Gegenangebot",
        cancelled: "storniert", completed: "abgeschlossen", expired: "abgelaufen",
    };
    return (
        <div className="bg-white border border-[#FFCC00]/40 rounded-sm overflow-hidden animate-in fade-in-50 slide-in-from-top-2" data-testid={`kpi-drilldown-${kpi}`}>
            <div className="px-5 py-3 border-b border-gray-100 bg-[#FFCC00]/5 flex items-center justify-between gap-2">
                <h3 className="font-display font-bold text-base flex items-center gap-2">
                    <span className="text-[#FFCC00]">▾</span> {titles[kpi]}
                </h3>
                <div className="flex items-center gap-2">
                    {linkInfo && (
                        <Link to={linkInfo.to} className="text-[10px] uppercase tracking-wider font-bold text-blue-700 hover:underline" data-testid={`kpi-drilldown-link-${kpi}`}>
                            {linkInfo.label} →
                        </Link>
                    )}
                    <button onClick={onClose} className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-[#111827]" data-testid={`kpi-drilldown-close-${kpi}`}>
                        Schließen ✕
                    </button>
                </div>
            </div>
            <div className="p-4">
                {/* Modus 1: response_time → ResponseTrendChart inline */}
                {kpi === "response_time" && <ResponseTrendChart />}

                {/* Modus 2: conversion → Erklärung */}
                {kpi === "conversion" && (
                    <div className="text-sm text-gray-700 space-y-2" data-testid="kpi-conversion-explanation">
                        <p>
                            <span className="font-bold">Conversion {extra?.conversionRate ?? 0}%</span> bedeutet:
                            Von Ihren {extra?.totalCount ?? 0} Inseraten haben{" "}
                            {extra?.totalCount > 0
                                ? Math.round((extra.conversionRate / 100) * extra.totalCount)
                                : 0}{" "}
                            mindestens eine Reservierung erhalten.
                        </p>
                        <p className="text-[12px] text-gray-500">
                            Formel: <span className="font-mono-num">Reservierungen ÷ Inserate × 100</span>.
                            Eine höhere Conversion bedeutet, dass mehr Inserate ernsthafte Käuferanfragen
                            generieren. Anhaltspunkt: ≥ 30% gilt als sehr gut.
                        </p>
                    </div>
                )}

                {/* Modus 3-9: Listen-Render */}
                {!["response_time", "conversion"].includes(kpi) && (
                    <>
                        {loading && <div className="text-sm text-gray-500 py-4 text-center">Lade Details …</div>}
                        {!loading && (!items || items.length === 0) && (
                            <div className="text-sm text-gray-500 py-6 text-center" data-testid={`kpi-drilldown-empty-${kpi}`}>
                                Keine Einträge vorhanden.
                            </div>
                        )}
                        {!loading && items && items.length > 0 && (
                            <ul className="divide-y divide-gray-100" data-testid={`kpi-drilldown-list-${kpi}`}>
                                {/* Listing-basierte KPIs */}
                                {["views", "active", "favorites", "auctions"].includes(kpi) &&
                                    items.map((l, i) => (
                                        <li key={l.id} className="flex items-center gap-3 py-2.5" data-testid={`drilldown-row-${l.id}`}>
                                            <div className="font-mono-num font-black text-base text-gray-300 w-6 shrink-0">{i + 1}</div>
                                            <Link to={`/listing/${l.id}`} className="w-12 h-12 shrink-0 bg-gray-100 rounded-sm overflow-hidden" target="_blank" rel="noopener noreferrer">
                                                {l.image ? <img src={l.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><Package className="w-4 h-4" /></div>}
                                            </Link>
                                            <div className="flex-1 min-w-0">
                                                <Link to={`/listing/${l.id}`} className="font-bold text-sm text-[#111827] hover:underline block truncate" target="_blank" rel="noopener noreferrer">{l.title}</Link>
                                                <div className="text-xs text-gray-500 mt-0.5 font-mono-num">
                                                    {l.price ? formatPrice(l.price) : "—"}
                                                    {l.sold && <span className="ml-2 inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-sm text-[9px] uppercase font-bold tracking-wider">verkauft</span>}
                                                    {kpi === "auctions" && l.auction_end && (
                                                        <span className="ml-2 text-[10px] text-amber-700">bis {new Date(l.auction_end).toLocaleDateString("de-DE")}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                {kpi === "views" && (<><div className="font-mono-num font-black text-base text-[#111827]">{l.views.toLocaleString("de-DE")}</div><div className="text-[9px] uppercase text-gray-500 font-bold tracking-wider">Views</div></>)}
                                                {kpi === "favorites" && (<><div className="font-mono-num font-black text-base text-pink-700">{l.favorites}</div><div className="text-[9px] uppercase text-gray-500 font-bold tracking-wider">Favoriten</div></>)}
                                                {kpi === "active" && (<><div className="font-mono-num font-black text-base text-[#111827]">{l.views.toLocaleString("de-DE")}</div><div className="text-[9px] uppercase text-gray-500 font-bold tracking-wider">Views</div></>)}
                                                {kpi === "auctions" && (<><div className="font-mono-num font-black text-base text-amber-700">{l.current_bid ? formatPrice(l.current_bid) : "—"}</div><div className="text-[9px] uppercase text-gray-500 font-bold tracking-wider">Gebot</div></>)}
                                            </div>
                                        </li>
                                    ))}
                                {/* Offers */}
                                {kpi === "offers" && items.map((o) => (
                                    <li key={o.id} className="flex items-center justify-between py-2.5" data-testid={`drilldown-row-${o.id}`}>
                                        <div className="min-w-0 flex-1">
                                            <Link to={`/listing/${o.listing_id}`} className="font-bold text-sm text-[#111827] hover:underline block truncate" target="_blank" rel="noopener noreferrer">{o.listing_title}</Link>
                                            <div className="text-[11px] text-gray-500 font-mono-num">
                                                {o.buyer_name || "Käufer"} · {o.created_at ? new Date(o.created_at).toLocaleDateString("de-DE") : "—"}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0 ml-3">
                                            <div className="font-mono-num font-bold text-[#111827]">{o.amount ? formatPrice(o.amount) : "—"}</div>
                                            <div className={`text-[10px] uppercase font-bold tracking-wider ${o.status === "accepted" ? "text-green-700" : o.status === "rejected" ? "text-red-700" : "text-amber-700"}`}>{STATUS_DE[o.status] || o.status}</div>
                                        </div>
                                    </li>
                                ))}
                                {/* Reservations */}
                                {kpi === "reservations" && items.map((r) => (
                                    <li key={r.id} className="flex items-center justify-between py-2.5" data-testid={`drilldown-row-${r.id}`}>
                                        <div className="min-w-0 flex-1">
                                            <Link to={`/listing/${r.listing_id}`} className="font-bold text-sm text-[#111827] hover:underline block truncate" target="_blank" rel="noopener noreferrer">{r.listing_title}</Link>
                                            <div className="text-[11px] text-gray-500 font-mono-num">
                                                {r.buyer_name || "Käufer"} · res. {r.reserved_at ? new Date(r.reserved_at).toLocaleDateString("de-DE") : "—"}
                                                {r.expires_at && ` · läuft ab ${new Date(r.expires_at).toLocaleDateString("de-DE")}`}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0 ml-3">
                                            {r.amount && <div className="font-mono-num font-bold text-[#111827]">{formatPrice(r.amount)}</div>}
                                            <div className={`text-[10px] uppercase font-bold tracking-wider ${r.status === "completed" ? "text-green-700" : r.status === "cancelled" || r.status === "expired" ? "text-red-700" : "text-amber-700"}`}>{STATUS_DE[r.status] || r.status}</div>
                                        </div>
                                    </li>
                                ))}
                                {/* Reviews */}
                                {kpi === "reviews" && items.map((rv) => (
                                    <li key={rv.id} className="py-2.5" data-testid={`drilldown-row-${rv.id}`}>
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                                <div className="font-mono-num font-black text-base text-amber-600">{"★".repeat(rv.rating)}<span className="text-gray-300">{"★".repeat(5 - rv.rating)}</span></div>
                                                <div className="text-[11px] text-gray-500 font-mono-num">
                                                    {rv.customer_name || "Kunde"} · {rv.created_at ? new Date(rv.created_at).toLocaleDateString("de-DE") : "—"}
                                                </div>
                                            </div>
                                            {rv.has_response && <span className="text-[10px] uppercase font-bold tracking-wider text-green-700">Beantwortet</span>}
                                        </div>
                                        {rv.comment_excerpt && (
                                            <div className="text-sm text-gray-700 mt-1 italic">&bdquo;{rv.comment_excerpt}&ldquo;</div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default function DealerStats({ sections = ["sales", "workshop", "finance"] }) {
    const want = (s) => sections.includes(s);
    const [data, setData] = useState(null);
    const [points, setPoints] = useState(null);
    const [billing, setBilling] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [workshop, setWorkshop] = useState(null);
    const [invoiceStats, setInvoiceStats] = useState(null);
    const [financeLoaded, setFinanceLoaded] = useState(false);
    const [error, setError] = useState(null);
    // Phase 3.32 — Zeitraum-Filter (ISO YYYY-MM-DD); leer = alle Daten.
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [reloading, setReloading] = useState(false);
    // Feb-2026 — Drill-Down-Panel pro KPI-Card.
    const [selectedKpi, setSelectedKpi] = useState(null);
    const [drilldownData, setDrilldownData] = useState({});  // {kpi: items[]}
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    // Inner-Collapse: Punkte-Aufschlüsselung (Default eingeklappt).
    const [pointsExpanded, setPointsExpanded] = useState(false);

    const toggleKpi = async (kpi) => {
        if (selectedKpi === kpi) { setSelectedKpi(null); return; }
        setSelectedKpi(kpi);
        if (["active", "sold", "favorites", "auctions", "offers", "reservations", "reviews"].includes(kpi) && !drilldownData[kpi]) {
            setDrilldownLoading(true);
            try {
                const r = await api.get(`/users/me/dealer-stats/drilldown?kpi=${kpi}`);
                setDrilldownData((prev) => ({ ...prev, [kpi]: r.data?.items || [] }));
            } catch {
                setDrilldownData((prev) => ({ ...prev, [kpi]: [] }));
            } finally {
                setDrilldownLoading(false);
            }
        }
    };

    const loadStats = async () => {
        setReloading(true);
        try {
            const q = new URLSearchParams();
            if (dateFrom) q.set("date_from", dateFrom);
            if (dateTo) q.set("date_to", dateTo);
            const qs = q.toString() ? `?${q.toString()}` : "";
            const jobs = [];
            if (want("sales")) jobs.push(api.get(`/users/me/dealer-stats${qs}`).then((r) => setData(r.data)));
            if (want("workshop")) jobs.push(api.get(`/users/me/dealer-stats/workshop${qs}`).then((r) => setWorkshop(r.data)).catch(() => setWorkshop(null)));
            if (want("finance")) jobs.push(api.get(`/users/me/dealer-stats/invoices${qs}`).then((r) => setInvoiceStats(r.data)).catch(() => setInvoiceStats(null)));
            await Promise.all(jobs);
        } catch (e) {
            setError(e.response?.data?.detail || "Fehler beim Laden");
        } finally {
            setReloading(false);
        }
    };

    const loadFinance = async () => {
        await Promise.allSettled([
            api.get("/users/me/points").then((r) => setPoints(r.data)),
            api.get("/users/me/tariff/billing-preview").then((r) => setBilling(r.data)),
            api.get("/users/me/invoices").then((r) => setInvoices(r.data.items || [])),
        ]);
        setFinanceLoaded(true);
    };

    useEffect(() => {
        loadStats();
        if (want("finance")) loadFinance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const downloadInvoicePdf = async (inv) => {
        try {
            const res = await api.get(`/users/me/invoices/${inv.id}/pdf`, { responseType: "blob" });
            previewPdfResponse(res, (inv.invoice_number || inv.id) + ".pdf");
        } catch (e) {
            // silent
        }
    };

    const downloadStatsCsv = async () => {
        try {
            const q = new URLSearchParams();
            if (dateFrom) q.set("date_from", dateFrom);
            if (dateTo) q.set("date_to", dateTo);
            const qs = q.toString() ? `?${q.toString()}` : "";
            const res = await api.get(`/users/me/dealer-stats/export.csv${qs}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.download = `dealer-stats-${today}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            // silent — fail soft
        }
    };

    const downloadStatsPdf = async () => {
        try {
            const q = new URLSearchParams();
            if (dateFrom) q.set("date_from", dateFrom);
            if (dateTo) q.set("date_to", dateTo);
            const qs = q.toString() ? `?${q.toString()}` : "";
            const res = await api.get(`/users/me/dealer-stats/export.pdf${qs}`, { responseType: "blob" });
            const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            previewPdfResponse(res, `dealer-stats-${today}.pdf`);
        } catch (e) {
            // silent — fail soft
        }
    };

    if (error) {
        return <div className="text-sm text-red-600" data-testid="dealer-stats-error">{error}</div>;
    }
    if (want("sales") && !data) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="dealer-stats-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Statistiken werden geladen…
            </div>
        );
    }

    // Sales-abgeleitete Werte (nur wenn data geladen).
    const listings = data?.listings;
    const conversionRate = data && listings && listings.total > 0
        ? Math.round((data.reservations.total / listings.total) * 100)
        : 0;

    const noFinance = !(invoiceStats && invoiceStats.finalized_total > 0)
        && !(points && points.balance)
        && !(billing && billing.tariff)
        && invoices.length === 0;

    return (
        <div className="space-y-6" data-testid="dealer-stats">
            {/* ============ VERKAUF: Inserate-/Performance-Statistik ============ */}
            {want("sales") && data && (
                <Section
                    collapseKey="sales_stats"
                    icon={TrendingUp}
                    title="Statistik & Performance"
                    testid="sales-stats-section"
                >
                    <div className="p-5 space-y-6">
                        {/* Zeitraum-Filter + Export */}
                        <div className="flex flex-wrap items-end gap-2">
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Von</label>
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="rounded-sm border border-gray-300 px-2 py-1.5 text-xs font-mono-num bg-white"
                                    data-testid="stats-date-from"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Bis</label>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="rounded-sm border border-gray-300 px-2 py-1.5 text-xs font-mono-num bg-white"
                                    data-testid="stats-date-to"
                                />
                            </div>
                            <button
                                onClick={loadStats}
                                disabled={reloading}
                                className="px-3 py-1.5 rounded-sm border border-[#111827] bg-[#111827] text-white hover:bg-black text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                                data-testid="stats-date-apply"
                            >
                                {reloading ? "Lädt…" : "Anwenden"}
                            </button>
                            {(dateFrom || dateTo) && (
                                <button
                                    onClick={() => { setDateFrom(""); setDateTo(""); setTimeout(loadStats, 0); }}
                                    className="px-3 py-1.5 rounded-sm border border-gray-300 bg-white hover:bg-gray-50 text-xs font-bold uppercase tracking-wider"
                                    data-testid="stats-date-reset"
                                >
                                    Zurücksetzen
                                </button>
                            )}
                            <a
                                href="#"
                                onClick={(e) => { e.preventDefault(); downloadStatsCsv(); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#111827] bg-white hover:bg-[#111827] hover:text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer"
                                data-testid="dealer-stats-csv-export"
                                title="KPI-Übersicht als CSV-Datei herunterladen"
                            >
                                <Download className="w-3.5 h-3.5" /> CSV
                            </a>
                            <a
                                href="#"
                                onClick={(e) => { e.preventDefault(); downloadStatsPdf(); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#DC2626] bg-white hover:bg-[#DC2626] hover:text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer text-[#DC2626]"
                                data-testid="dealer-stats-pdf-export"
                                title="KPI-Übersicht als PDF herunterladen"
                            >
                                <FileText className="w-3.5 h-3.5" /> PDF
                            </a>
                        </div>

                        {/* Phase 3.33 — Monatlicher PDF-Versand Toggle */}
                        <MonthlyStatsEmailToggle />

                        {/* Jun-2026 — Wöchentliche Compliance-Lücken-Übersicht Toggle */}
                        <ComplianceWeeklyEmailToggle />

                        {/* Jul-2026 — Compliance-Score-Widget ein-/ausblenden */}
                        <ComplianceWidgetToggle />

                        {/* Feb-2026 — Best-Practice-Empfehlungen */}
                        <DealerTipsCard />

                        {/* Top KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <StatCard
                                icon={Package}
                                label="Aktive Inserate"
                                value={listings.active}
                                sub={`${listings.total} gesamt · ${listings.sold} verkauft`}
                                testid="kpi-active-listings"
                                onClick={() => toggleKpi("active")}
                                active={selectedKpi === "active"}
                            />
                            <StatCard
                                icon={Eye}
                                label="Views gesamt"
                                value={listings.views_total.toLocaleString("de-DE")}
                                sub={listings.active > 0 ? `Ø ${Math.round(listings.views_total / listings.active)} pro Inserat` : "—"}
                                testid="kpi-views"
                                onClick={() => toggleKpi("views")}
                                active={selectedKpi === "views"}
                            />
                            <StatCard
                                icon={Heart}
                                label="Favorisierungen"
                                value={data.favorites_total}
                                sub="Über alle Ihre Inserate"
                                testid="kpi-favorites"
                                onClick={() => toggleKpi("favorites")}
                                active={selectedKpi === "favorites"}
                            />
                            <StatCard
                                icon={Gavel}
                                label="Auktionen"
                                value={listings.auctions}
                                sub={listings.auctions ? "aktiv inseriert" : "Keine"}
                                testid="kpi-auctions"
                                onClick={() => toggleKpi("auctions")}
                                active={selectedKpi === "auctions"}
                            />
                        </div>

                        {["active", "views", "favorites", "auctions"].includes(selectedKpi) && (
                            <KpiDrilldownPanel
                                kpi={selectedKpi}
                                onClose={() => setSelectedKpi(null)}
                                loading={drilldownLoading}
                                items={selectedKpi === "views" ? data.top_listings : (drilldownData[selectedKpi] || [])}
                            />
                        )}

                        {/* Engagement KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <StatCard
                                icon={Handshake}
                                label="Preisvorschläge"
                                value={data.offers.total}
                                sub={`${data.offers.by_status.pending} offen · ${data.offers.by_status.accepted} angenommen`}
                                tone="accent"
                                testid="kpi-offers"
                                onClick={() => toggleKpi("offers")}
                                active={selectedKpi === "offers"}
                            />
                            <StatCard
                                icon={Clock}
                                label="Reservierungen"
                                value={data.reservations.total}
                                sub={`${data.reservations.by_status.pending} aktiv · ${data.reservations.by_status.cancelled} storniert`}
                                tone="accent"
                                testid="kpi-reservations"
                                onClick={() => toggleKpi("reservations")}
                                active={selectedKpi === "reservations"}
                            />
                            <StatCard
                                icon={Star}
                                label="Bewertungen"
                                value={data.reviews.count > 0 ? `${data.reviews.avg} ★` : "—"}
                                sub={data.reviews.count > 0 ? `${data.reviews.count} Bewertung${data.reviews.count === 1 ? "" : "en"}` : "Noch keine Bewertungen"}
                                tone={data.reviews.count > 0 ? "success" : "muted"}
                                testid="kpi-reviews"
                                onClick={() => toggleKpi("reviews")}
                                active={selectedKpi === "reviews"}
                            />
                            <StatCard
                                icon={MessageSquare}
                                label="Reaktionszeit"
                                value={data.reviews.response_avg_hours != null ? (data.reviews.response_avg_hours < 1 ? "<1 h" : `${data.reviews.response_avg_hours} h`) : "—"}
                                sub={data.reviews.responded_count > 0 ? `${data.reviews.responded_count} Antwort${data.reviews.responded_count === 1 ? "" : "en"}${data.reviews.responds_fast ? " · schnell" : ""}` : "Noch keine Antworten"}
                                tone={data.reviews.responds_fast ? "success" : (data.reviews.response_avg_hours != null ? "accent" : "muted")}
                                testid="kpi-response-time"
                                onClick={() => toggleKpi("response_time")}
                                active={selectedKpi === "response_time"}
                            />
                            <StatCard
                                icon={TrendingUp}
                                label="Conversion"
                                value={`${conversionRate}%`}
                                sub="Reservierungen pro Inserat"
                                tone="success"
                                testid="kpi-conversion"
                                onClick={() => toggleKpi("conversion")}
                                active={selectedKpi === "conversion"}
                            />
                        </div>

                        {["offers", "reservations", "reviews", "response_time", "conversion"].includes(selectedKpi) && (
                            <KpiDrilldownPanel
                                kpi={selectedKpi}
                                onClose={() => setSelectedKpi(null)}
                                loading={drilldownLoading}
                                items={drilldownData[selectedKpi] || []}
                                extra={{
                                    offers: data.offers,
                                    reservations: data.reservations,
                                    reviews: data.reviews,
                                    conversionRate,
                                    activeCount: listings.active,
                                    totalCount: listings.total,
                                }}
                            />
                        )}
                    </div>
                </Section>
            )}

            {/* ============ WERKSTATT: Auftrags-Übersicht ============ */}
            {want("workshop") && workshop && workshop.total_orders > 0 && (
                <Section
                    collapseKey="workshop_stats"
                    icon={Wrench}
                    title="Werkstatt-Übersicht"
                    right={<Link to="/dealer/workshop-orders" className="text-blue-700 hover:underline" data-testid="workshop-stats-link">zu den Aufträgen →</Link>}
                    testid="workshop-stats-block"
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5">
                        <StatCard
                            icon={Wrench}
                            label="Aufträge gesamt"
                            value={workshop.total_orders}
                            sub={`${workshop.by_status.invoiced} abgerechnet`}
                            testid="kpi-workshop-total"
                        />
                        <StatCard
                            icon={Clock}
                            label="Offene Aufträge"
                            value={workshop.open_orders}
                            sub={`${workshop.by_status.in_progress} in Arbeit · ${workshop.by_status.confirmed} bestätigt`}
                            tone="accent"
                            testid="kpi-workshop-open"
                        />
                        <StatCard
                            icon={TrendingUp}
                            label="Auftragswert Ø"
                            value={`${(workshop.avg_gross || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`}
                            sub="brutto pro Auftrag"
                            testid="kpi-workshop-avg"
                        />
                        <StatCard
                            icon={Coins}
                            label="Werkstatt-Umsatz"
                            value={(workshop.total_gross || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                            sub="brutto, gefiltert"
                            tone="success"
                            testid="kpi-workshop-revenue"
                        />
                    </div>
                    {workshop.top_customers && workshop.top_customers.length > 0 && (
                        <div className="border-t border-gray-100">
                            <div className="px-5 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">Top-Werkstattkunden</div>
                            <ul className="divide-y divide-gray-100">
                                {workshop.top_customers.map((c) => (
                                    <li key={c.customer_id} className="flex items-center justify-between px-5 py-2 text-sm" data-testid={`workshop-top-cust-${c.customer_id}`}>
                                        <div className="font-bold text-[#111827] truncate">{c.name}</div>
                                        <div className="text-right shrink-0 ml-3">
                                            <div className="font-mono-num font-bold text-[#111827]">{c.orders} Aufträge</div>
                                            <div className="text-[11px] font-mono-num text-gray-500">{c.gross_total.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Section>
            )}

            {/* ============ RECHNUNGEN: Finanz-Blöcke ============ */}
            {want("finance") && !financeLoaded && (
                <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="finance-stats-loading">
                    <Loader2 className="w-4 h-4 animate-spin" /> Finanzdaten werden geladen…
                </div>
            )}

            {want("finance") && financeLoaded && noFinance && (
                <div className="bg-white border border-dashed border-gray-300 rounded-sm p-8 text-center text-sm text-gray-500" data-testid="finance-stats-empty">
                    Noch keine Finanzdaten vorhanden. Rechnungen, €Punkte und Monatsabrechnung erscheinen hier, sobald Buchungen vorliegen.
                </div>
            )}

            {/* Rechnungs-Übersicht (invoiceStats) */}
            {want("finance") && invoiceStats && invoiceStats.finalized_total > 0 && (
                <Section
                    collapseKey="invoice_stats"
                    icon={Receipt}
                    title="Rechnungs-Übersicht"
                    badge={invoiceStats.overdue_count || 0}
                    badgeLabel="überfällig"
                    right={<Link to="/dealer/invoices" className="text-blue-700 hover:underline" data-testid="invoice-stats-link">zu den Rechnungen →</Link>}
                    testid="invoice-stats-block"
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5">
                        <StatCard
                            icon={Receipt}
                            label="Umsatz gesamt"
                            value={(invoiceStats.total_gross || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                            sub={`netto ${(invoiceStats.total_net || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`}
                            tone="success"
                            testid="kpi-inv-revenue"
                        />
                        <StatCard
                            icon={Wrench}
                            label="Werkstatt-Rechnungen"
                            value={invoiceStats.workshop.count}
                            sub={(invoiceStats.workshop.gross_total || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                            testid="kpi-inv-workshop"
                        />
                        <StatCard
                            icon={Package}
                            label="Fahrzeug-Rechnungen"
                            value={invoiceStats.vehicle.count}
                            sub={(invoiceStats.vehicle.gross_total || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                            testid="kpi-inv-vehicle"
                        />
                        <StatCard
                            icon={AlertCircle}
                            label="Offene Beträge"
                            value={(invoiceStats.open_amount || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                            sub={invoiceStats.overdue_count > 0
                                ? `${invoiceStats.overdue_count} überfällig (${(invoiceStats.overdue_amount || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })})`
                                : "keine überfällig"}
                            tone={invoiceStats.overdue_count > 0 ? "accent" : "muted"}
                            testid="kpi-inv-open"
                        />
                    </div>
                </Section>
            )}

            {/* €Punkte-Saldo */}
            {want("finance") && points && points.balance && (
                <Section
                    collapseKey="points"
                    icon={Coins}
                    title="€Punkte-Saldo"
                    right="Aktivitätsprotokoll"
                    testid="dealer-points-block"
                >
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-5 border-b border-gray-100">
                        <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/40 rounded-sm p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Gesamt €Punkte</div>
                            <div className="font-display font-black text-2xl font-mono-num" data-testid="points-total">
                                {points.balance.total.points >= 0 ? "+" : ""}{points.balance.total.points}
                            </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">€-Wert</div>
                            <div className="font-display font-black text-2xl font-mono-num" data-testid="points-euro">
                                {points.balance.total.euro >= 0 ? "+" : ""}{points.balance.total.euro.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Buchungen</div>
                            <div className="font-display font-black text-2xl font-mono-num">{points.balance.total.count}</div>
                        </div>
                    </div>
                    {((Object.keys(points.balance.by_bereich || {}).length > 0) || (points.recent && points.recent.length > 0)) && (
                        <div className="border-b border-gray-100 px-5 py-2">
                            <button
                                onClick={() => setPointsExpanded((v) => !v)}
                                className="w-full flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] py-1"
                                data-testid="points-expand-toggle"
                                aria-expanded={pointsExpanded}
                            >
                                <span>{pointsExpanded ? "▾" : "▸"} Bereich-Aufschlüsselung &amp; letzte Buchungen</span>
                                <span className="text-[10px] text-gray-400 font-mono-num">
                                    {Object.keys(points.balance.by_bereich || {}).length} Bereiche · {(points.recent || []).length} Buchungen
                                </span>
                            </button>
                        </div>
                    )}
                    {pointsExpanded && Object.keys(points.balance.by_bereich || {}).length > 0 && (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="text-left p-2 pl-5">Bereich</th>
                                    <th className="text-right p-2">€Punkte</th>
                                    <th className="text-right p-2 pr-5">€-Wert</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(points.balance.by_bereich).map(([bk, b]) => (
                                    <tr key={bk} className="border-t border-gray-100" data-testid={`points-bereich-${bk}`}>
                                        <td className="p-2 pl-5 font-bold">{b.label}</td>
                                        <td className="p-2 text-right font-mono-num">
                                            <span className={b.points >= 0 ? "text-green-700" : "text-red-700"}>{b.points >= 0 ? "+" : ""}{b.points}</span>
                                        </td>
                                        <td className="p-2 pr-5 text-right font-mono-num">
                                            <span className={b.euro >= 0 ? "text-green-700" : "text-red-700"}>{b.euro >= 0 ? "+" : ""}{b.euro.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {pointsExpanded && points.recent && points.recent.length > 0 && (
                        <div className="border-t border-gray-100">
                            <div className="px-5 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">Letzte Buchungen</div>
                            <ul className="divide-y divide-gray-100">
                                {points.recent.slice(0, 10).map((e) => (
                                    <li key={e.id} className="flex items-center justify-between px-5 py-2 text-sm">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-bold text-[#111827] truncate">{e.label}</div>
                                            <div className="text-[11px] text-gray-500 font-mono-num">{new Date(e.created_at).toLocaleString("de-DE")} · {e.bereich}</div>
                                        </div>
                                        <div className="text-right shrink-0 ml-3">
                                            <div className={`font-mono-num font-bold ${e.points >= 0 ? "text-green-700" : "text-red-700"}`}>
                                                {e.points >= 0 ? "+" : ""}{e.points} €Pkt
                                            </div>
                                            <div className={`text-[11px] font-mono-num ${e.euro_value >= 0 ? "text-green-700" : "text-red-700"}`}>
                                                {e.euro_value >= 0 ? "+" : ""}{e.euro_value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Section>
            )}

            {/* Monatsabrechnung — Vorschau */}
            {want("finance") && billing && billing.tariff && (
                <Section
                    collapseKey="billing"
                    icon={Receipt}
                    title="Monatsabrechnung — Vorschau"
                    right="Vorläufig"
                    testid="billing-preview-block"
                >
                    <p className="text-[11px] text-gray-500 px-5 pt-3">Aktueller Monat <span className="font-mono-num">{billing.period}</span> · Inklusive €Punkte-Verrechnung</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-5">
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Tarif: {billing.tariff.name}</div>
                            <div className="font-display font-black text-2xl font-mono-num" data-testid="billing-base">
                                {billing.tariff.base_amount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </div>
                            <div className="text-[11px] text-gray-500 mt-1">Grundpreis</div>
                        </div>
                        <div className={`border rounded-sm p-3 ${billing.points_credit_euro >= 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">€Punkte ({billing.points_count >= 0 ? "+" : ""}{billing.points_count})</div>
                            <div className={`font-display font-black text-2xl font-mono-num ${billing.points_credit_euro >= 0 ? "text-green-700" : "text-red-700"}`} data-testid="billing-credit">
                                {billing.points_credit_euro >= 0 ? "−" : "+"}{Math.abs(billing.points_credit_euro).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </div>
                            <div className="text-[11px] text-gray-500 mt-1">{billing.points_credit_euro >= 0 ? "Gutschrift" : "Belastung"}</div>
                        </div>
                        <div className="bg-[#FFCC00]/15 border border-[#FFCC00]/40 rounded-sm p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Netto-Rechnung</div>
                            <div className="font-display font-black text-2xl font-mono-num" data-testid="billing-net">
                                {billing.net_amount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </div>
                            <div className="text-[11px] text-gray-500 mt-1">Endbetrag</div>
                        </div>
                    </div>
                    {billing.points_count !== 0 && Object.keys(billing.by_bereich).length > 0 && (
                        <div className="border-t border-gray-100 px-5 py-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Verrechnung nach Bereich</div>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(billing.by_bereich).map(([bk, b]) => (
                                    <span key={bk} className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm border text-xs ${b.euro >= 0 ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                                        <span className="font-bold">{b.label}</span>
                                        <span className="font-mono-num">{b.euro >= 0 ? "+" : ""}{b.euro.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </Section>
            )}

            {/* Meine Rechnungen (Default eingeklappt) */}
            {want("finance") && invoices && invoices.length > 0 && (
                <Section
                    collapseKey="my_invoices"
                    defaultCollapsed
                    icon={FileText}
                    title="Meine Rechnungen"
                    right={`${invoices.length} insgesamt`}
                    testid="my-invoices-block"
                >
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="text-left p-2 pl-5">Rechnungsnummer</th>
                                <th className="text-left p-2">Zeitraum</th>
                                <th className="text-right p-2">Grundpreis</th>
                                <th className="text-right p-2">€Punkte</th>
                                <th className="text-right p-2">Netto</th>
                                <th className="text-center p-2">Status</th>
                                <th className="text-right p-2 pr-5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <tr key={inv.id} className="border-t border-gray-100" data-testid={`my-invoice-row-${inv.id}`}>
                                    <td className="p-2 pl-5 font-mono text-[11px]">{inv.invoice_number}</td>
                                    <td className="p-2 font-mono-num">{inv.period}</td>
                                    <td className="p-2 text-right font-mono-num">
                                        {(inv.base_amount || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </td>
                                    <td className="p-2 text-right font-mono-num">
                                        <span className={inv.points_credit_euro >= 0 ? "text-green-700" : "text-red-700"}>
                                            {inv.points_credit_euro >= 0 ? "−" : "+"}{Math.abs(inv.points_credit_euro || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                        </span>
                                    </td>
                                    <td className="p-2 text-right font-mono-num font-bold">
                                        {(inv.net_amount || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </td>
                                    <td className="p-2 text-center">
                                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${
                                            inv.status === "dispatched" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                                        }`}>
                                            {inv.status === "dispatched" ? "versandt" : "entwurf"}
                                        </span>
                                    </td>
                                    <td className="p-2 pr-5 text-right">
                                        <button
                                            onClick={() => downloadInvoicePdf(inv)}
                                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-300 hover:bg-gray-100 rounded-sm text-[10px] font-bold uppercase tracking-wider text-gray-700"
                                            data-testid={`my-invoice-pdf-${inv.id}`}
                                        >
                                            <Download className="w-3 h-3" /> PDF
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>
            )}
        </div>
    );
}
